import { Input, Label } from '@slal/ui/forms';
import { Col, Row } from '@slal/ui/layout';
import { Controller, useFormContext } from '@slal/ui/react-hook-form/core';
import { ShowWhen } from '@slal/ui/react-hook-form/utils';
import { useEffect, useRef } from 'react';
import { NumericFormat } from 'react-number-format';
import {
    Card,
    Field,
    StyledCombobox,
    StyledInputGroup,
    StyledRadioGroup,
    Switch,
    TextField,
    useAnalytics,
} from '~/components';
import { isAllowedRange } from '~/utils';
import { commissionTypeOptions, drawdownOptions, productOptions, strings } from './strings';
import { StyledCol, StyledField, StyledText } from './styles';

export const InvestmentDetailsCard = () => {
    const { watch, setValue, control, clearErrors } = useFormContext();

    const { pushEvent, hasFiredAnalytics } = useAnalytics();
    const productName = watch('productName');
    const drawdownType = watch('drawdownType');
    const drawdown = watch('drawdown');
    const contributions = watch('contributions');

    const prevDrawdownType = useRef(drawdownType);

    useEffect(() => {
        if (productName && !hasFiredAnalytics) {
            const selectedProduct = productOptions.find(option => option.value === productName);
            pushEvent({
                eventAction: 'Select a product',
                eventLabel: selectedProduct?.value,
            });
        }
    }, [productName, pushEvent]);

    useEffect(() => {
        if (productName === 'postRetirement') {
            setValue('contributions', '');
            setValue('contributionsAmount', '');
        } else if (productName === 'preRetirement') {
            setValue('drawdown', '');
            setValue('drawdownAmount', '');
            setValue('drawdownPercent', '');
        }
    }, [productName]);

    useEffect(() => {
        if (!contributions && !drawdown) {
            return;
        }

        if (hasFiredAnalytics) return;

        const selectedProduct = productOptions.find(option => option.value === productName);

        if (contributions && selectedProduct?.value === 'preRetirement') {
            pushEvent({
                eventAction: selectedProduct?.value,
                eventLabel: `Contributions selected- ${contributions}`,
            });
        }

        if (drawdown && selectedProduct?.value === 'postRetirement') {
            pushEvent({
                eventAction: selectedProduct?.value,
                eventLabel: `Drawdown selected- ${drawdown}`,
            });
        }
    }, [contributions, drawdown, productName]);

    useEffect(() => {
        if (prevDrawdownType.current === drawdownType) {
            return;
        }
        prevDrawdownType.current = drawdownType;
        setValue('drawdownPercent', '');
        setValue('drawdownAmount', '');
        clearErrors(['drawdownPercent']);
        pushEvent({
            eventAction: 'Drawdown amount',
            eventLabel: `Option selected- ${drawdownType === 'percent' ? 'percentage' : 'amount'}`,
        });
    }, [drawdownType]);

    return (
        <Card heading={strings.clientDetails}>
            <Field label={strings.clientName} labelFor="clientName">
                <TextField maxLength={80} name="clientName" size={5} />
            </Field>
            <Field label={strings.selectProduct} labelFor="productName">
                <StyledCombobox name="productName" options={productOptions} />
            </Field>
            <ShowWhen fieldName="productName" valueEquals={values => values === productOptions[0].value}>
                <Field label={strings.contributions} labelFor="contributions">
                    <StyledRadioGroup name="contributions" options={drawdownOptions} stacked />
                </Field>
                <ShowWhen fieldName="contributions" valueEquals="Yes">
                    <Field label={strings.drawdownAmount}>
                        <Row>
                            <StyledCol col="12">
                                <StyledInputGroup>
                                    <Label>{strings.currencySymbol}</Label>
                                    <Controller
                                        name="contributionsAmount"
                                        control={control}
                                        render={({ field }) => (
                                            <NumericFormat
                                                {...field}
                                                name="contributionsAmount"
                                                id="contributionsAmount"
                                                onBlur={() => {
                                                    pushEvent({
                                                        eventAction: 'Contributions amount',
                                                        eventLabel: `Value entered- amount`,
                                                    });
                                                }}
                                                customInput={Input}
                                                valueIsNumericString
                                                allowNegative={false}
                                                isAllowed={isAllowedRange(1, 99999999)}
                                                autoComplete="off"
                                                aria-label={`${strings.drawdownAmount} in pounds`}
                                            />
                                        )}
                                    />
                                </StyledInputGroup>
                            </StyledCol>
                            <StyledCol col="12">
                                <StyledText>
                                    For more information see the{' '}
                                    <a
                                        onClick={() => {
                                            pushEvent({
                                                eventAction: 'Drawdown amount link click',
                                                eventLabel: `Active Money SIPP key features document`,
                                            });
                                        }}
                                        target="_blank"
                                        href="https://www.standardlife.co.uk/adviser/individual-pensions/sipp"
                                    >
                                        Active Money SIPP key features document
                                    </a>
                                </StyledText>
                            </StyledCol>
                        </Row>
                    </Field>
                </ShowWhen>
            </ShowWhen>
            <ShowWhen fieldName="productName" valueEquals={values => values === productOptions[1].value}>
                <Field label={strings.drawdown}>
                    <StyledRadioGroup name="drawdown" options={drawdownOptions} stacked />
                </Field>
                <ShowWhen fieldName="drawdown" valueEquals="Yes">
                    <Field label={strings.drawdownAmount}>
                        <Row>
                            <StyledCol col="12">
                                <StyledInputGroup>
                                    <ShowWhen fieldName="drawdownType" valueEquals="amount">
                                        <Label>{strings.currencySymbol}</Label>
                                        <Controller
                                            name="drawdownAmount"
                                            control={control}
                                            render={({ field }) => (
                                                <NumericFormat
                                                    {...field}
                                                    name="drawdownAmount"
                                                    id="drawdownAmount"
                                                    customInput={Input}
                                                    valueIsNumericString
                                                    onBlur={() => {
                                                        pushEvent({
                                                            eventAction: 'Drawdown amount',
                                                            eventLabel: `Value entered- amount`,
                                                        });
                                                    }}
                                                    allowNegative={false}
                                                    isAllowed={isAllowedRange(1, 99999999)}
                                                    autoComplete="off"
                                                    aria-label={`${strings.drawdownAmount} in pounds`}
                                                />
                                            )}
                                        />
                                    </ShowWhen>
                                    <ShowWhen fieldName="drawdownType" valueEquals="percent">
                                        <Controller
                                            name="drawdownPercent"
                                            control={control}
                                            rules={{
                                                validate: value =>
                                                    parseFloat(value) <= 100 || strings.drawdownValidationMessage,
                                            }}
                                            render={({ field }) => (
                                                <NumericFormat
                                                    {...field}
                                                    name="drawdownPercent"
                                                    id="drawdownPercent"
                                                    onBlur={() => {
                                                        pushEvent({
                                                            eventAction: 'Drawdown amount',
                                                            eventLabel: `Value entered- percentage`,
                                                        });
                                                    }}
                                                    customInput={Input}
                                                    valueIsNumericString
                                                    allowNegative={false}
                                                    aria-label={`${strings.drawdownAmount} in percentage`}
                                                />
                                            )}
                                        />
                                        <Label>{strings.percentageSymbol}</Label>
                                    </ShowWhen>
                                </StyledInputGroup>
                                <Switch fieldName="drawdownType" options={commissionTypeOptions} />
                            </StyledCol>
                            <Col col="12">
                                <StyledField errorFor="drawdownPercent" />
                            </Col>
                            <StyledCol col="12">
                                <StyledText>
                                    For more information see the{' '}
                                    <a
                                        onClick={() => {
                                            pushEvent({
                                                eventAction: 'Drawdown amount link click',
                                                eventLabel: `Active Money SIPP key features document`,
                                            });
                                        }}
                                        target="_blank"
                                        href="https://www.standardlife.co.uk/adviser/individual-pensions/sipp"
                                    >
                                        Active Money SIPP key features document
                                    </a>
                                </StyledText>
                            </StyledCol>
                        </Row>
                    </Field>
                </ShowWhen>
            </ShowWhen>
        </Card>
    );
};
